import _ from 'lodash';
// import moment from 'moment';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import BookingField from './BookingDrawerField.react';
import Currencies from 'arbitrip-common/general/utils/Currencies';
import { ReactComponent as PointsIcon } from '../../../img/search_page/loyalty/present-plain.svg';
import { PAYMENT_TYPE, PAYMENT_STATUS } from 'arbitrip-common/general/constants';

// While a BNPL payment is still awaiting its charge, payment.sum is only the booking-time
// quote — the frozen fee-inclusive USD amount is the meaningful number to show. Once charged,
// payment.sum / payment.currency hold the real amount actually collected.
const BNPL_AWAITING_CHARGE_STATUSES = [PAYMENT_STATUS.BNPL_PENDING, PAYMENT_STATUS.BNPL_RETRY_SCHEDULED];

const BookingDrawerPaymentDetails = ({ booking, retroactivePointsUsageEnabled, openRetroactiveModal, profile }) => {
	const { i18n, t } = useTranslation();

	if (!booking || !booking.payment) {
		return null;
	}
	const { deal, payment } = booking;

	const credit_l4_digit = useMemo(() => {
		return _.get(payment, 'payment_method.credit_l4_digit', '');
	}, [payment]);

	const payment_type_value = useMemo(() => {
		let type = _.get(payment, 'payment_type');
		if (!type) return '';

		if (type === 'credit2000') {
			type = 'credit card'
		}

		const translationKey = `PaymentTypes.${_.startCase(type)}`;

		if (i18n.exists(translationKey) && !_.isEmpty(t(translationKey))) {
			return t(translationKey);
		}

		return _.startCase(type);
	}, [payment, t, i18n]);

	const points_used = payment?.loyalty?.points ?? 0;
	const points_used_value = payment?.loyalty?.points_in_currency ?? 0;
	const points_used_currency = payment?.loyalty?.currency;

	const payment_sum = payment?.sum ?? 0;
	const isBnpl = payment.payment_type === PAYMENT_TYPE.BNPL;
	const isBnplAwaitingCharge = isBnpl && BNPL_AWAITING_CHARGE_STATUSES.includes(payment.status);
	const bnplUsdAmountRaw = payment?.bnpl?.usd_amount_with_fee;
	const bnplUsdAmount = isBnplAwaitingCharge && bnplUsdAmountRaw > 0 ? Math.round(bnplUsdAmountRaw) : null;

	const has_installments =
		payment.payment_type === PAYMENT_TYPE.CREDIT_CARD &&
		payment.installment &&
		payment.installment.num_installments > 1;

	const show_points = points_used > 0 || retroactivePointsUsageEnabled;

	const numberOfInstallments = payment.installment?.num_installments;

	return (
		<BookingField
			data-testid="booking-drawer-payment-details"
			fieldTitle={t('PaymentDetails')}
			fieldValue={
				<React.Fragment>
					<div className="payment-details">
						{payment.payment_type && <div>{t('PaymentType')}</div>}
						{payment_sum > 0 && <div>{t('Sum')}</div>}
					</div>
					<div className="payment-details bold">
						{payment.payment_type && (
							<div>
								{payment_type_value}
								{credit_l4_digit && ` (${credit_l4_digit})`}
							</div>
						)}
						{payment_sum > 0 && (
							<div>
								{bnplUsdAmount != null
									? Currencies.getPriceWithCurrency(bnplUsdAmount, 'USD')
									: Currencies.getPriceWithDisplayCurrencyByPrecision(payment_sum, 1, payment.currency, 2)}
							</div>
						)}
					</div>
					<div className="payment-details bold">
						{has_installments && <div>{t('NumInstallments', { numberOfInstallments })}</div>}
					</div>
					<div className="payment-details">
						{show_points && <div>{t('PointsUsed')}</div>}
						{show_points && <div>{t('PointsValue')}</div>}
					</div>
					<div className="payment-details bold">
						{show_points && <div>{points_used}</div>}
						{show_points && (
							<div>
								{Currencies.getPriceWithDisplayCurrencyByPrecision(
									points_used_value,
									1,
									points_used_currency,
									2,
								)}
							</div>
						)}
						{show_points &&
							(retroactivePointsUsageEnabled ? (
								<div className="booking-points-update">
									<Button variant="outlined" fullWidth onClick={openRetroactiveModal}>
										{t('UpdatePoints')}
									</Button>
								</div>
							) : (
								<div className="booking-points-icon">
									<PointsIcon />
								</div>
							))}
					</div>
				</React.Fragment>
			}
		/>
	);
};

export default BookingDrawerPaymentDetails;
